import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useSubscriptionItems} from '../hooks/useSubscriptionItems.ts';
import {useSubscriptionItemsByPrice} from '../hooks/useSubscriptionItemsByPrice.ts';
import {useSubscriptionItemsSelection} from '../hooks/useSubscriptionItemsSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/SubscriptionItemsService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {SubscriptionItem} from '../types';
import {useQueryClient} from '@tanstack/react-query';

// FNP-331: optional priceId prop switches the table to cross-subscription
// "items by price" mode. priceId and subscriptionId are mutually exclusive;
// the parent picks one. Use distinct wrapper components so each branch's
// hook is the only one called at render time — keeps hook order stable
// and avoids the throw-on-empty-id guards in either hook.
interface RecordsTableProps {
    subscriptionId?: string;
    priceId?: string;
}

export const RecordsTable = ({subscriptionId, priceId}: RecordsTableProps) => {
    if (priceId) {
        return <RecordsTableByPrice priceId={priceId}/>;
    }
    return <RecordsTableBySubscription subscriptionId={subscriptionId || ''}/>;
};

const RecordsTableBySubscription = ({subscriptionId}: {subscriptionId: string}) => {
    const {
        items,
        totalCount,
        loading,
        nextToken,
        loadMoreRecords,
        handleFilterByKeyword
    } = useSubscriptionItems(subscriptionId);
    return <RecordsTableInner
        items={items}
        totalCount={totalCount}
        loading={loading}
        nextToken={nextToken}
        loadMoreRecords={loadMoreRecords}
        handleFilterByKeyword={handleFilterByKeyword}
    />;
};

const RecordsTableByPrice = ({priceId}: {priceId: string}) => {
    const {
        items,
        totalCount,
        loading,
        nextToken,
        loadMoreRecords,
        handleFilterByKeyword
    } = useSubscriptionItemsByPrice(priceId);
    return <RecordsTableInner
        items={items}
        totalCount={totalCount}
        loading={loading}
        nextToken={nextToken}
        loadMoreRecords={loadMoreRecords}
        handleFilterByKeyword={handleFilterByKeyword}
    />;
};

interface RecordsTableInnerProps {
    items: SubscriptionItem[];
    totalCount: number;
    loading: boolean;
    nextToken: string | null;
    loadMoreRecords: () => void;
    handleFilterByKeyword: (k: string) => void;
}

const RecordsTableInner = ({
    items,
    totalCount,
    loading,
    nextToken,
    loadMoreRecords,
    handleFilterByKeyword
}: RecordsTableInnerProps) => {

    const {selectedRecords, setSelectedRecords} = useSubscriptionItemsSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<SubscriptionItem | null>(null);
    const queryClient = useQueryClient();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: SubscriptionItem) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: SubscriptionItem) => {
        if (selectedRecord?.id) {
            const {created: _created, updated: _updated, price: _price, createdByUsername: _createdByUsername, createdBy: _createdBy, ...cleanData} = values;
            await update(selectedRecord.id, cleanData as SubscriptionItem);
            queryClient.invalidateQueries({queryKey: ['subscription-items']});
        }
    };

    const {columns} = useTableColumns({onRecordClick: handleRecordClick});

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => loadMoreRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={loadMoreRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
